<div class="p-6">
  <!-- Toast notifications -->
  <p-toast position="top-right"></p-toast>

  <!-- Page Header -->
  <div class="mb-6">
    <h1 class="page-title mb-2">
      @if (projectId === '1') {
      {{ 'projects.addProject' | translate }}
      } @else {
      {{ 'projects.editProject' | translate }}
      }
    </h1>
    <p class="page-subtitle">
      @if (projectId === '1') {
      {{ 'projects.addProjectSubtitle' | translate }}
      } @else {
      {{ 'projects.editProjectSubtitle' | translate }}
      }
    </p>
  </div>

  <!-- Main Content Layout -->
  <div class="w-full">
    <form [formGroup]="projectForm" (ngSubmit)="onSubmit()">
      <!-- Project Information Layout -->
      <div class="space-y-8 mb-8">
        <!-- Project Header Card -->
        <p-card>
          <h3 class="section-title mb-6">{{ 'projectEdit.projectInformation' | translate }}</h3>
          <!-- Main Layout: Single Column -->
          <div class="flex flex-col gap-8">
            <!-- Row 1: Image + Name/Owner Block -->
            <div class="flex items-start gap-6">
              <!-- Project Icon/Logo Upload -->
              <div class="flex-shrink-0">
                @if (loading()) {
                <!-- Skeleton for project image -->
                <p-skeleton width="90px" height="90px" class="rounded-xl"></p-skeleton>
                } @else {
                <div
                  class="relative w-[90px] h-[90px] rounded-xl border-2 border-gray-600 overflow-hidden cursor-pointer group"
                  (click)="triggerImageUpload()"
                >
                  @if (projectImagePreview()) {
                  <!-- Uploaded Image Display -->
                  <img
                    [src]="projectImagePreview()"
                    alt="Project Logo"
                    class="w-full h-full object-cover"
                  />

                  <!-- Edit Overlay on Hover -->
                  <div class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div class="text-white text-center">
                      <i class="pi pi-camera text-xl mb-1 block"></i>
                      <span class="text-caption-sm">{{
                        'projectEdit.changeImage' | translate
                      }}</span>
                    </div>
                  </div>
                  } @else {
                  <!-- Upload Placeholder -->
                  <div class="w-full h-full bg-surface-100 dark:bg-surface-800 flex items-center justify-center">
                    <i class="pi pi-image text-3xl text-surface-400"></i>
                  </div>

                  <!-- Upload Text on Hover -->
                  <div class="absolute inset-0 bg-primary/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white">
                    <i class="pi pi-upload text-xl mb-1"></i>
                    <span class="text-caption-sm">{{
                      'projectEdit.uploadImageButton' | translate
                    }}</span>
                  </div>
                  }

                  <!-- Hidden File Input -->
                  <input
                    #fileInput
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                    (change)="onImageFileChange($event)"
                    class="hidden"
                    aria-label="Upload project image"
                  />
                </div>

                <!-- Upload Status/Error Messages -->
                @if (projectImageError) {
                <div class="mt-2 max-w-[90px]">
                  <p-message severity="error">{{ projectImageError | translate }}</p-message>
                </div>
                }
                }
              </div>

              <!-- Name and Owner Column -->
              <div class="flex-1 max-w-2xl">
                @if (loading()) {
                <!-- Skeleton for Project Name -->
                <div class="mb-6">
                  <label class="form-label mb-2 block">{{ 'projectEdit.projectName' | translate }}</label>
                  <p-skeleton width="100%" height="3.5rem" class="rounded-md"></p-skeleton>
                </div>
                <!-- Skeleton for Owner Section -->
                <div class="space-y-6">
                  <div>
                    <label class="form-label mb-2 block">{{ 'projectEdit.ownerName' | translate }}</label>
                    <p-skeleton width="100%" height="3.5rem" class="rounded-md"></p-skeleton>
                  </div>
                  <div>
                    <label class="form-label mb-2 block">{{ 'projectEdit.ownerUrl' | translate }}</label>
                    <p-skeleton width="100%" height="3.5rem" class="rounded-md"></p-skeleton>
                  </div>
                </div>
                } @else {
                <!-- Project Name -->
                <div class="mb-6">
                  <label for="projectName" class="form-label mb-2 block">{{ 'projectEdit.projectName' | translate }}</label>
                  <input id="projectName" type="text" pInputText formControlName="name" class="w-full" />
                  @if (projectForm.get('name')?.invalid && projectForm.get('name')?.touched) {
                  <div class="mt-2">
                    <p-message severity="error">{{ 'projectEdit.nameRequired' | translate }}</p-message>
                  </div>
                  }
                </div>

                <!-- Project Owner -->
                <div class="space-y-6">
                  <div>
                    <label for="ownerName" class="form-label mb-2 block">{{ 'projectEdit.ownerName' | translate }}</label>
                    <input id="ownerName" type="text" pInputText formControlName="ownerName" class="w-full" />
                    @if (projectForm.get('ownerName')?.invalid &&
                    projectForm.get('ownerName')?.touched) {
                    <div class="mt-2">
                      <p-message severity="error">{{ 'projectEdit.ownerNameRequired' | translate }}</p-message>
                    </div>
                    }
                  </div>
                  <div>
                    <label for="ownerUrl" class="form-label mb-2 block">{{ 'projectEdit.ownerUrl' | translate }}</label>
                    <input id="ownerUrl" type="url" pInputText formControlName="ownerUrl" class="w-full" />
                    @if (projectForm.get('ownerUrl')?.invalid &&
                    projectForm.get('ownerUrl')?.touched) {
                    <div class="mt-2">
                      <p-message severity="error">{{ 'projectEdit.invalidUrl' | translate }}</p-message>
                    </div>
                    }
                  </div>
                </div>
                }
              </div>
            </div>

            <!-- Row 2: Project Description -->
            @if (loading()) {
            <div>
              <label class="form-label mb-2 block">{{ 'projectEdit.description' | translate }}</label>
              <p-skeleton width="100%" height="7rem" class="rounded-md"></p-skeleton>
            </div>
            } @else {
            <div>
              <label for="description" class="form-label mb-2 block">{{ 'projectEdit.description' | translate }}</label>
              <textarea
                id="description"
                pInputText
                formControlName="description"
                rows="5"
                class="w-full resize-none"
              ></textarea>
              @if (projectForm.get('description')?.invalid &&
              projectForm.get('description')?.touched) {
              <div class="mt-2">
                <p-message severity="error">{{ 'projectEdit.descriptionRequired' | translate }}</p-message>
              </div>
              }
            </div>
            }
            <!-- Row 3: Location -->
            @if (loading()) {
            <div class="flex items-center gap-4">
              <div class="flex items-center gap-2">
                <i class="pi pi-map-marker text-muted"></i>
                <div>
                  <label class="form-label mb-2 block">{{ 'projectEdit.location' | translate }}</label>
                  <p-skeleton width="200px" height="3.5rem" class="rounded-md"></p-skeleton>
                </div>
              </div>
            </div>
            } @else {
            <div class="flex items-center gap-4 flex-wrap">
              <div class="flex items-center gap-2">
                <i class="pi pi-map-marker text-muted"></i>
                <div>
                  <label for="location" class="form-label mb-2 block">{{ 'projectEdit.location' | translate }}</label>
                  <input id="location" type="text" pInputText formControlName="location" />
                </div>
              </div>
            </div>
            }

            <!-- Row 4: Social Links -->
            @if (loading()) {
            <div class="flex items-center gap-4 flex-wrap">
              <p-skeleton width="80px" height="1.5rem" class="rounded"></p-skeleton>
              <!-- Twitter -->
              <div class="flex items-center gap-1">
                <p-skeleton width="1rem" height="1rem" class="rounded"></p-skeleton>
                <p-skeleton width="224px" height="3rem" class="rounded-md"></p-skeleton>
              </div>
              <!-- GitHub -->
              <div class="flex items-center gap-1">
                <p-skeleton width="1rem" height="1rem" class="rounded"></p-skeleton>
                <p-skeleton width="224px" height="3rem" class="rounded-md"></p-skeleton>
              </div>
              <!-- Telegram -->
              <div class="flex items-center gap-1">
                <p-skeleton width="1rem" height="1rem" class="rounded"></p-skeleton>
                <p-skeleton width="224px" height="3rem" class="rounded-md"></p-skeleton>
              </div>
              <!-- Discord -->
              <div class="flex items-center gap-1">
                <p-skeleton width="1rem" height="1rem" class="rounded"></p-skeleton>
                <p-skeleton width="224px" height="3rem" class="rounded-md"></p-skeleton>
              </div>
            </div>
            } @else {
            <div class="flex items-center gap-4 flex-wrap">
              <label class="text-label-md">{{ 'projectEdit.followOn' | translate }}</label>

              <!-- Twitter -->
              <div class="flex items-center gap-1">
                <i class="pi pi-twitter text-info"></i>
                <input
                  type="url"
                  pInputText
                  formControlName="twitterUrl"
                  [placeholder]="'projectEdit.twitterPlaceholder' | translate"
                  class="w-56"
                />
              </div>

              <!-- GitHub -->
              <div class="flex items-center gap-1">
                <i class="pi pi-github text-muted"></i>
                <input
                  type="url"
                  pInputText
                  formControlName="githubUrl"
                  [placeholder]="'projectEdit.githubPlaceholder' | translate"
                  class="w-56"
                />
              </div>

              <!-- Telegram -->
              <div class="flex items-center gap-1">
                <i class="pi pi-send text-info"></i>
                <input
                  type="url"
                  pInputText
                  formControlName="telegramUrl"
                  [placeholder]="'projectEdit.telegramPlaceholder' | translate"
                  class="w-56"
                />
              </div>

              <!-- Discord -->
              <div class="flex items-center gap-1">
                <i class="pi pi-discord text-brand-primary"></i>
                <input
                  type="url"
                  pInputText
                  formControlName="discordUrl"
                  [placeholder]="'projectEdit.discordPlaceholder' | translate"
                  class="w-56"
                />
              </div>
            </div>

            <!-- Social Links Validation Messages -->
            @if (projectForm.get('twitterUrl')?.invalid && projectForm.get('twitterUrl')?.touched) {
            <div class="mt-2">
              <p-message severity="error">{{ 'projectEdit.invalidUrl' | translate }}</p-message>
            </div>
            } @if (projectForm.get('githubUrl')?.invalid && projectForm.get('githubUrl')?.touched) {
            <div class="mt-2">
              <p-message severity="error">{{ 'projectEdit.invalidUrl' | translate }}</p-message>
            </div>
            } @if (projectForm.get('telegramUrl')?.invalid &&
            projectForm.get('telegramUrl')?.touched) {
            <div class="mt-2">
              <p-message severity="error">{{ 'projectEdit.invalidUrl' | translate }}</p-message>
            </div>
            } @if (projectForm.get('discordUrl')?.invalid && projectForm.get('discordUrl')?.touched)
            {
            <div class="mt-2">
              <p-message severity="error">{{ 'projectEdit.invalidUrl' | translate }}</p-message>
            </div>
            } }

            <!-- Row 5: Documentation Links -->
            @if (loading()) {
            <div class="flex items-center gap-4 flex-wrap">
              <p-skeleton width="100px" height="1.5rem" class="rounded"></p-skeleton>
              <!-- Whitepaper -->
              <div class="flex items-center gap-1">
                <p-skeleton width="1rem" height="1rem" class="rounded"></p-skeleton>
                <p-skeleton width="224px" height="3rem" class="rounded-md"></p-skeleton>
              </div>
              <!-- Tokenomics -->
              <div class="flex items-center gap-1">
                <p-skeleton width="1rem" height="1rem" class="rounded"></p-skeleton>
                <p-skeleton width="224px" height="3rem" class="rounded-md"></p-skeleton>
              </div>
            </div>
            } @else {
            <div class="flex items-center gap-4 flex-wrap">
              <label class="text-label-md">{{ 'projectEdit.documentation' | translate }}</label>

              <!-- Whitepaper -->
              <div class="flex items-center gap-1">
                <i class="pi pi-file-pdf text-error"></i>
                <input
                  type="url"
                  pInputText
                  formControlName="whitepaperUrl"
                  [placeholder]="'projectEdit.whitepaperPlaceholder' | translate"
                  class="w-56"
                />
              </div>

              <!-- Tokenomics -->
              <div class="flex items-center gap-1">
                <i class="pi pi-chart-pie text-brand-primary"></i>
                <input
                  type="url"
                  pInputText
                  formControlName="tokenomicsUrl"
                  [placeholder]="'projectEdit.tokenomicsPlaceholder' | translate"
                  class="w-56"
                />
              </div>
            </div>

            <!-- Documentation Links Validation Messages -->
            @if (projectForm.get('whitepaperUrl')?.invalid &&
            projectForm.get('whitepaperUrl')?.touched) {
            <div class="mt-2">
              <p-message severity="error">{{ 'projectEdit.invalidUrl' | translate }}</p-message>
            </div>
            } @if (projectForm.get('tokenomicsUrl')?.invalid &&
            projectForm.get('tokenomicsUrl')?.touched) {
            <div class="mt-2">
              <p-message severity="error">{{ 'projectEdit.invalidUrl' | translate }}</p-message>
            </div>
            } }
          </div>
        </p-card>

        <!-- Token Information Card -->
        <p-card>
          <h3 class="section-title mb-6">{{ 'projectEdit.tokenInformation' | translate }}</h3>

          @if (loading()) {
          <div class="space-y-3">
            <!-- Token Name Row -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <p-skeleton width="80px" height="1.5rem" class="rounded"></p-skeleton>
              <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
            </div>
            <!-- Token Symbol Row -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <p-skeleton width="60px" height="1.5rem" class="rounded"></p-skeleton>
              <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
            </div>
            <!-- Token Address Row -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <p-skeleton width="70px" height="1.5rem" class="rounded"></p-skeleton>
              <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
            </div>
            <!-- Total Supply Row -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <p-skeleton width="90px" height="1.5rem" class="rounded"></p-skeleton>
              <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
            </div>
            <!-- Swap On Row -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <p-skeleton width="70px" height="1.5rem" class="rounded"></p-skeleton>
              <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
            </div>
            <!-- Claim On Row -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <p-skeleton width="70px" height="1.5rem" class="rounded"></p-skeleton>
              <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
            </div>
            <!-- Public Sale Price Row -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <p-skeleton width="140px" height="1.5rem" class="rounded"></p-skeleton>
              <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
            </div>
            <!-- Private Sale Price Row -->
            <div class="flex justify-between items-center py-2">
              <p-skeleton width="140px" height="1.5rem" class="rounded"></p-skeleton>
              <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
            </div>
          </div>
          } @else {
          <div class="space-y-3">
            <!-- Name -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <label class="form-label">{{ 'projectEdit.tokenName' | translate }}</label>
              <div class="max-w-xs">
                <input
                  type="text"
                  pInputText
                  formControlName="tokenName"
                  [placeholder]="'projectEdit.tokenNamePlaceholder' | translate"
                  class="w-full"
                />
                @if (projectForm.get('tokenName')?.invalid && projectForm.get('tokenName')?.touched)
                {
                <div class="mt-2">
                  <p-message severity="error">{{
                    getErrorMessage(
                      projectForm.get('tokenName'),
                      'projectEdit.tokenName' | translate
                    )
                  }}</p-message>
                </div>
                }
              </div>
            </div>

            <!-- Symbol -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <label class="form-label">{{ 'projectEdit.tokenSymbol' | translate }}</label>
              <div class="max-w-xs">
                <input
                  type="text"
                  pInputText
                  formControlName="tokenSymbol"
                  [placeholder]="'projectEdit.tokenSymbolPlaceholder' | translate"
                  class="w-full"
                />
                @if (projectForm.get('tokenSymbol')?.invalid &&
                projectForm.get('tokenSymbol')?.touched) {
                <div class="mt-2">
                  <p-message severity="error">{{
                    getErrorMessage(
                      projectForm.get('tokenSymbol'),
                      'projectEdit.tokenSymbol' | translate
                    )
                  }}</p-message>
                </div>
                }
              </div>
            </div>

            <!-- Address -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <label class="form-label">{{ 'projectEdit.tokenAddress' | translate }}</label>
              <div class="max-w-xs">
                <input
                  type="text"
                  pInputText
                  formControlName="tokenAddress"
                  [placeholder]="'projectEdit.tokenAddressPlaceholder' | translate"
                  class="w-full"
                />
                @if (projectForm.get('tokenAddress')?.invalid &&
                projectForm.get('tokenAddress')?.touched) {
                <div class="mt-2">
                  <p-message severity="error">{{
                    getErrorMessage(
                      projectForm.get('tokenAddress'),
                      'projectEdit.tokenAddress' | translate
                    )
                  }}</p-message>
                </div>
                }
              </div>
            </div>

            <!-- Total Supply -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <label class="form-label">{{ 'projectEdit.totalSupply' | translate }}</label>
              <div class="max-w-xs">
                <p-inputGroup class="w-full">
                  <p-inputNumber
                    formControlName="totalSupply"
                    [placeholder]="'projectEdit.totalSupplyPlaceholder' | translate"
                    [showButtons]="false"
                    [useGrouping]="true"
                    class="text-right"
                  />
                  <p-inputGroupAddon>{{
                    projectForm.get('tokenSymbol')?.value || 'PTK'
                  }}</p-inputGroupAddon>
                </p-inputGroup>
                @if (projectForm.get('totalSupply')?.invalid &&
                projectForm.get('totalSupply')?.touched) {
                <div class="mt-2">
                  <p-message severity="error">{{
                    getErrorMessage(
                      projectForm.get('totalSupply'),
                      'projectEdit.totalSupply' | translate
                    )
                  }}</p-message>
                </div>
                }
              </div>
            </div>

            <!-- Swap On -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <label class="form-label">{{ 'projectEdit.swapOn' | translate }}</label>
              <div class="max-w-xs">
                <div class="flex items-center gap-2">
                  <div class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                    <i class="pi pi-ethereum text-white text-xs"></i>
                  </div>
                  <input
                    type="text"
                    pInputText
                    formControlName="swapOn"
                    [placeholder]="'projectEdit.swapOnPlaceholder' | translate"
                    class="w-full"
                  />
                </div>
                @if (projectForm.get('swapOn')?.invalid && projectForm.get('swapOn')?.touched) {
                <div class="mt-2">
                  <p-message severity="error">{{ getErrorMessage(projectForm.get('swapOn'), 'projectEdit.swapOn' | translate) }}</p-message>
                </div>
                }
              </div>
            </div>

            <!-- Claim On -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <label class="form-label">{{ 'projectEdit.claimOn' | translate }}</label>
              <div class="max-w-xs">
                <div class="flex items-center gap-2">
                  <div class="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                    <i class="pi pi-circle text-white text-xs"></i>
                  </div>
                  <input
                    type="text"
                    pInputText
                    formControlName="claimOn"
                    [placeholder]="'projectEdit.claimOnPlaceholder' | translate"
                    class="w-full"
                  />
                </div>
                @if (projectForm.get('claimOn')?.invalid && projectForm.get('claimOn')?.touched) {
                <div class="mt-2">
                  <p-message severity="error">{{ getErrorMessage(projectForm.get('claimOn'), 'projectEdit.claimOn' | translate) }}</p-message>
                </div>
                }
              </div>
            </div>

            <!-- Public Sale Price Supply -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <label class="form-label">{{
                'projectEdit.publicSalePriceSupply' | translate
              }}</label>
              <div class="max-w-xs">
                <p-inputGroup class="w-full">
                  <p-inputGroupAddon>{{
                    'projectEdit.oneUsdtEquals' | translate
                  }}</p-inputGroupAddon>
                  <p-inputNumber
                    formControlName="publicSalePrice"
                    [placeholder]="'projectEdit.publicSalePricePlaceholder' | translate"
                    [showButtons]="false"
                    [useGrouping]="true"
                    class="text-right"
                  />
                  <p-inputGroupAddon>{{
                    projectForm.get('tokenSymbol')?.value || 'PTK'
                  }}</p-inputGroupAddon>
                </p-inputGroup>
                @if (projectForm.get('publicSalePrice')?.invalid &&
                projectForm.get('publicSalePrice')?.touched) {
                <div class="mt-2">
                  <p-message severity="error">{{
                    getErrorMessage(
                      projectForm.get('publicSalePrice'),
                      'projectEdit.publicSalePriceSupply' | translate
                    )
                  }}</p-message>
                </div>
                }
              </div>
            </div>

            <!-- Private Sale Price Supply -->
            <div class="flex justify-between items-center py-2">
              <label class="form-label">{{
                'projectEdit.privateSalePriceSupply' | translate
              }}</label>
              <div class="max-w-xs">
                <p-inputGroup class="w-full">
                  <p-inputGroupAddon>{{
                    'projectEdit.oneUsdtEquals' | translate
                  }}</p-inputGroupAddon>
                  <p-inputNumber
                    formControlName="privateSalePrice"
                    [placeholder]="'projectEdit.privateSalePricePlaceholder' | translate"
                    [showButtons]="false"
                    [useGrouping]="true"
                    class="text-right"
                  />
                  <p-inputGroupAddon>{{
                    projectForm.get('tokenSymbol')?.value || 'PTK'
                  }}</p-inputGroupAddon>
                </p-inputGroup>
                @if (projectForm.get('privateSalePrice')?.invalid &&
                projectForm.get('privateSalePrice')?.touched) {
                <div class="mt-2">
                  <p-message severity="error">{{
                    getErrorMessage(
                      projectForm.get('privateSalePrice'),
                      'projectEdit.privateSalePriceSupply' | translate
                    )
                  }}</p-message>
                </div>
                }
              </div>
            </div>
          </div>
          }
        </p-card>

        <!-- Sale Information Card -->
        <p-card>
          <h3 class="section-title mb-6">{{ 'projectEdit.saleInformation' | translate }}</h3>

          @if (loading()) {
          <div class="space-y-3">
            <!-- SoftCap Row -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <p-skeleton width="60px" height="1.5rem" class="rounded"></p-skeleton>
              <p-skeleton width="180px" height="3rem" class="rounded-md"></p-skeleton>
            </div>
            <!-- HardCap Row -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <p-skeleton width="60px" height="1.5rem" class="rounded"></p-skeleton>
              <p-skeleton width="180px" height="3rem" class="rounded-md"></p-skeleton>
            </div>
            <!-- Min/Max Allocation Row -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <p-skeleton width="120px" height="1.5rem" class="rounded"></p-skeleton>
              <div class="flex items-center gap-2">
                <p-skeleton width="100px" height="3rem" class="rounded-md"></p-skeleton>
                <span class="text-gray-400">-</span>
                <p-skeleton width="100px" height="3rem" class="rounded-md"></p-skeleton>
              </div>
            </div>
            <!-- Total Tokens for Sale Row -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <p-skeleton width="140px" height="1.5rem" class="rounded"></p-skeleton>
              <p-skeleton width="180px" height="3rem" class="rounded-md"></p-skeleton>
            </div>
            <!-- Pool Types Row -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <p-skeleton width="80px" height="1.5rem" class="rounded"></p-skeleton>
              <p-skeleton width="300px" height="3rem" class="rounded-md"></p-skeleton>
            </div>
            <!-- Unsold Tokens Row -->
            <div class="flex justify-between items-center py-2">
              <p-skeleton width="100px" height="1.5rem" class="rounded"></p-skeleton>
              <p-skeleton width="300px" height="3rem" class="rounded-md"></p-skeleton>
            </div>
          </div>
          } @else {
          <div class="space-y-3">
            <!-- SoftCap -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <label class="form-label">{{ 'projectEdit.softCap' | translate }}</label>
              <p-inputGroup class="max-w-xs">
                <p-inputNumber
                  formControlName="softCap"
                  [placeholder]="'projectEdit.softCapPlaceholder' | translate"
                  [showButtons]="false"
                  [useGrouping]="true"
                  class="text-right"
                />
                <p-inputGroupAddon>{{ 'projectEdit.usdt' | translate }}</p-inputGroupAddon>
              </p-inputGroup>
            </div>

            <!-- HardCap -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <label class="form-label">{{ 'projectEdit.hardCap' | translate }}</label>
              <p-inputGroup class="max-w-xs">
                <p-inputNumber
                  formControlName="hardCap"
                  [placeholder]="'projectEdit.hardCapPlaceholder' | translate"
                  [showButtons]="false"
                  [useGrouping]="true"
                  class="text-right"
                />
                <p-inputGroupAddon>{{ 'projectEdit.usdt' | translate }}</p-inputGroupAddon>
              </p-inputGroup>
            </div>

            <!-- Min/Max Allocation -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <label class="form-label">{{ 'projectEdit.minMaxAllocation' | translate }}</label>
              <div class="flex items-center gap-2 text-white text-sm">
                <p-inputGroup class="w-20">
                  <p-inputNumber
                    formControlName="minAllocation"
                    [placeholder]="'projectEdit.minAllocationPlaceholder' | translate"
                    [showButtons]="false"
                  />
                  <p-inputGroupAddon>{{ 'projectEdit.usdt' | translate }}</p-inputGroupAddon>
                </p-inputGroup>
                <span class="text-gray-400">{{ 'projectEdit.dash' | translate }}</span>
                <p-inputGroup class="w-24">
                  <p-inputNumber
                    formControlName="maxAllocation"
                    [placeholder]="'projectEdit.maxAllocationPlaceholder' | translate"
                    [showButtons]="false"
                  />
                  <p-inputGroupAddon>{{ 'projectEdit.usdt' | translate }}</p-inputGroupAddon>
                </p-inputGroup>
              </div>
            </div>
            <!-- Total Tokens for Sale -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <label class="form-label">{{ 'projectEdit.totalTokensForSale' | translate }}</label>
              <p-inputGroup class="max-w-xs">
                <p-inputNumber
                  formControlName="totalTokensForSale"
                  [placeholder]="'projectEdit.totalTokensForSalePlaceholder' | translate"
                  [showButtons]="false"
                  [useGrouping]="true"
                  class="text-right"
                />
                <p-inputGroupAddon>{{
                  projectForm.get('tokenSymbol')?.value || 'PTK'
                }}</p-inputGroupAddon>
              </p-inputGroup>
            </div>

            <!-- Pool Types -->
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <label class="form-label">{{ 'projectEdit.poolTypes' | translate }}</label>
              <input
                type="text"
                pInputText
                formControlName="poolTypes"
                [placeholder]="'projectEdit.poolTypesPlaceholder' | translate"
                class="text-right bg-transparent border-none text-white focus:ring-0 flex-1 max-w-2xl"
              />
            </div>

            <!-- Unsold Tokens -->
            <div class="flex justify-between items-center py-2">
              <label class="form-label">{{ 'projectEdit.unsoldTokens' | translate }}</label>
              <input
                type="text"
                pInputText
                formControlName="unsoldTokens"
                [placeholder]="'projectEdit.unsoldTokensPlaceholder' | translate"
                class="text-right bg-transparent border-none text-white focus:ring-0 flex-1 max-w-2xl"
              />
            </div>
          </div>
          }
        </p-card>

        <!-- Investment Rounds Card -->
        <p-card>
          <h3 class="section-title mb-6">{{ 'projectEdit.investmentRounds' | translate }}</h3>

          @if (loading()) {
          <div class="space-y-6">
            <!-- Private Sale Skeleton -->
            <div class="flex items-start gap-4">
              <div class="flex flex-col items-center">
                <p-skeleton width="2rem" height="2rem" class="rounded-full"></p-skeleton>
                <p-skeleton width="2px" height="4rem" class="mt-2"></p-skeleton>
              </div>
              <div class="flex-1 space-y-6">
                <p-skeleton width="120px" height="1.5rem" class="rounded"></p-skeleton>
                <div class="space-y-6">
                  <!-- Exchange Rate Label (always visible) -->
                  <div class="flex items-center gap-2">
                    <label class="form-label whitespace-nowrap">{{ 'projectEdit.oneUsdtEquals' | translate }}</label>
                    <p-skeleton width="200px" height="3rem" class="rounded-md"></p-skeleton>
                  </div>
                  <!-- Start Date -->
                  <div>
                    <label class="form-label mb-2 block">{{ 'projectEdit.startDateAndTime' | translate }}</label>
                    <p-skeleton width="100%" height="3rem" class="rounded-md max-w-sm"></p-skeleton>
                  </div>
                  <!-- End Date -->
                  <div>
                    <label class="form-label mb-2 block">{{ 'projectEdit.endDateAndTime' | translate }}</label>
                    <p-skeleton width="100%" height="3rem" class="rounded-md max-w-sm"></p-skeleton>
                  </div>
                </div>
              </div>
            </div>
            <!-- Public Sale Skeleton -->
            <div class="flex items-start gap-4">
              <div class="flex flex-col items-center">
                <p-skeleton width="2rem" height="2rem" class="rounded-full"></p-skeleton>
                <p-skeleton width="2px" height="4rem" class="mt-2"></p-skeleton>
              </div>
              <div class="flex-1 space-y-6">
                <p-skeleton width="120px" height="1.5rem" class="rounded"></p-skeleton>
                <div class="space-y-6">
                  <!-- Exchange Rate Label (always visible) -->
                  <div class="flex items-center gap-2">
                    <label class="form-label whitespace-nowrap">{{ 'projectEdit.oneUsdtEquals' | translate }}</label>
                    <p-skeleton width="200px" height="3rem" class="rounded-md"></p-skeleton>
                  </div>
                  <!-- Start Date -->
                  <div>
                    <label class="form-label mb-2 block">{{ 'projectEdit.startDateAndTime' | translate }}</label>
                    <p-skeleton width="100%" height="3rem" class="rounded-md max-w-sm"></p-skeleton>
                  </div>
                  <!-- End Date -->
                  <div>
                    <label class="form-label mb-2 block">{{ 'projectEdit.endDateAndTime' | translate }}</label>
                    <p-skeleton width="100%" height="3rem" class="rounded-md max-w-sm"></p-skeleton>
                  </div>
                </div>
              </div>
            </div>
            <!-- TGE Date Skeleton -->
            <div class="flex items-start gap-4">
              <p-skeleton width="2rem" height="2rem" class="rounded-full"></p-skeleton>
              <div class="flex-1 space-y-6">
                <p-skeleton width="100px" height="1.5rem" class="rounded"></p-skeleton>
                <div class="space-y-6">
                  <!-- TGE Price Label (always visible) -->
                  <div class="flex items-center gap-2">
                    <label class="form-label whitespace-nowrap">{{ 'projectEdit.oneUsdtEquals' | translate }}</label>
                    <p-skeleton width="200px" height="3rem" class="rounded-md"></p-skeleton>
                  </div>
                  <!-- TGE Date -->
                  <div>
                    <label class="form-label mb-2 block">{{ 'projectEdit.tgeDateAndTime' | translate }}</label>
                    <p-skeleton width="100%" height="3rem" class="rounded-md max-w-sm"></p-skeleton>
                  </div>
                </div>
              </div>
            </div>
          </div>
          } @else {
          <p-stepper [value]="1" orientation="vertical">
            <!-- Private Sale Step -->
            <p-step-item [value]="1">
              <p-step>{{ 'projectEdit.privateSale' | translate }}</p-step>
              <p-step-panel>
                <ng-template #content let-activateCallback="activateCallback">
                  <div class="flex flex-col gap-6 py-4 pr-4">
                    <!-- Exchange Rate -->
                    <div class="flex items-center gap-2">
                      <label class="form-label whitespace-nowrap">{{
                        'projectEdit.oneUsdtEquals' | translate
                      }}</label>
                      <p-inputGroup class="max-w-[200px]">
                        <p-inputNumber
                          formControlName="privateSalePrice"
                          [showButtons]="false"
                          [useGrouping]="true"
                        />
                        <p-inputGroupAddon>{{
                          projectForm.get('tokenSymbol')?.value || 'PTK'
                        }}</p-inputGroupAddon>
                      </p-inputGroup>
                    </div>

                    <!-- Start Date -->
                    <div>
                      <label for="privateSaleStart" class="form-label mb-2 block">{{
                        'projectEdit.startDateAndTime' | translate
                      }}</label>
                      <p-datePicker
                        id="privateSaleStart"
                        formControlName="privateSaleStart"
                        [showTime]="true"
                        [hourFormat]="'24'"
                        dateFormat="dd/mm/yy"
                        class="w-full max-w-sm"
                      />
                      @if (projectForm.get('privateSaleStart')?.invalid &&
                      projectForm.get('privateSaleStart')?.touched) {
                      <div class="mt-2">
                        <p-message severity="error">{{
                          getErrorMessage(
                            projectForm.get('privateSaleStart'),
                            'projectEdit.startDateAndTime' | translate
                          )
                        }}</p-message>
                      </div>
                      }
                    </div>

                    <!-- End Date -->
                    <div>
                      <label for="privateSaleEnd" class="form-label mb-2 block">{{
                        'projectEdit.endDateAndTime' | translate
                      }}</label>
                      <p-datePicker
                        id="privateSaleEnd"
                        formControlName="privateSaleEnd"
                        [showTime]="true"
                        [hourFormat]="'24'"
                        dateFormat="dd/mm/yy"
                        class="w-full max-w-sm"
                      />
                      @if (projectForm.get('privateSaleEnd')?.invalid &&
                      projectForm.get('privateSaleEnd')?.touched) {
                      <div class="mt-2">
                        <p-message severity="error">{{
                          getErrorMessage(
                            projectForm.get('privateSaleEnd'),
                            'projectEdit.endDateAndTime' | translate
                          )
                        }}</p-message>
                      </div>
                      }
                    </div>
                  </div>

                  <div class="py-4">
                    <p-button
                      [label]="'projectEdit.next' | translate"
                      icon="pi pi-arrow-right"
                      iconPos="right"
                      (onClick)="activateCallback(2)"
                    />
                  </div>
                </ng-template>
              </p-step-panel>
            </p-step-item>

            <!-- Public Sale Step -->
            <p-step-item [value]="2">
              <p-step>{{ 'projectEdit.publicSale' | translate }}</p-step>
              <p-step-panel>
                <ng-template #content let-activateCallback="activateCallback">
                  <div class="flex flex-col gap-6 py-4 pr-4">
                    <!-- Exchange Rate -->
                    <div class="flex items-center gap-2">
                      <label class="form-label whitespace-nowrap">{{
                        'projectEdit.oneUsdtEquals' | translate
                      }}</label>
                      <p-inputGroup class="max-w-[200px]">
                        <p-inputNumber
                          formControlName="publicSalePrice"
                          [showButtons]="false"
                          [useGrouping]="true"
                        />
                        <p-inputGroupAddon>{{
                          projectForm.get('tokenSymbol')?.value || 'PTK'
                        }}</p-inputGroupAddon>
                      </p-inputGroup>
                    </div>

                    <!-- Start Date -->
                    <div>
                      <label for="publicSaleStart" class="form-label mb-2 block">{{
                        'projectEdit.startDateAndTime' | translate
                      }}</label>
                      <p-datePicker
                        id="publicSaleStart"
                        formControlName="publicSaleStart"
                        [showTime]="true"
                        [hourFormat]="'24'"
                        dateFormat="dd/mm/yy"
                        class="w-full max-w-sm"
                      />
                      @if (projectForm.get('publicSaleStart')?.invalid &&
                      projectForm.get('publicSaleStart')?.touched) {
                      <div class="mt-2">
                        <p-message severity="error">{{
                          getErrorMessage(
                            projectForm.get('publicSaleStart'),
                            'projectEdit.startDateAndTime' | translate
                          )
                        }}</p-message>
                      </div>
                      }
                    </div>

                    <!-- End Date -->
                    <div>
                      <label for="publicSaleEnd" class="form-label mb-2 block">{{
                        'projectEdit.endDateAndTime' | translate
                      }}</label>
                      <p-datePicker
                        id="publicSaleEnd"
                        formControlName="publicSaleEnd"
                        [showTime]="true"
                        [hourFormat]="'24'"
                        dateFormat="dd/mm/yy"
                        class="w-full max-w-sm"
                      />
                      @if (projectForm.get('publicSaleEnd')?.invalid &&
                      projectForm.get('publicSaleEnd')?.touched) {
                      <div class="mt-2">
                        <p-message severity="error">{{
                          getErrorMessage(
                            projectForm.get('publicSaleEnd'),
                            'projectEdit.endDateAndTime' | translate
                          )
                        }}</p-message>
                      </div>
                      }
                    </div>
                  </div>

                  <div class="flex gap-2 py-4">
                    <p-button
                      [label]="'projectEdit.back' | translate"
                      severity="secondary"
                      icon="pi pi-arrow-left"
                      (onClick)="activateCallback(1)"
                    />
                    <p-button
                      [label]="'projectEdit.next' | translate"
                      icon="pi pi-arrow-right"
                      iconPos="right"
                      (onClick)="activateCallback(3)"
                    />
                  </div>
                </ng-template>
              </p-step-panel>
            </p-step-item>

            <!-- TGE Date Step -->
            <p-step-item [value]="3">
              <p-step>{{ 'projectEdit.tgeDate' | translate }}</p-step>
              <p-step-panel>
                <ng-template #content let-activateCallback="activateCallback">
                  <div class="flex flex-col gap-6 py-4 pr-4">
                    <!-- TGE Price -->
                    <div class="flex items-center gap-2">
                      <label class="form-label whitespace-nowrap">{{
                        'projectEdit.oneUsdtEquals' | translate
                      }}</label>
                      <p-inputGroup class="max-w-[200px]">
                        <p-inputNumber
                          formControlName="tgePrice"
                          [showButtons]="false"
                          [useGrouping]="true"
                        />
                        <p-inputGroupAddon>{{
                          projectForm.get('tokenSymbol')?.value || 'PTK'
                        }}</p-inputGroupAddon>
                      </p-inputGroup>
                    </div>

                    <!-- TGE Date -->
                    <div>
                      <label for="tgeDate" class="form-label mb-2 block">{{ 'projectEdit.tgeDateAndTime' | translate }}</label>
                      <p-datePicker
                        id="tgeDate"
                        formControlName="tgeDate"
                        [showTime]="true"
                        [hourFormat]="'24'"
                        dateFormat="dd/mm/yy"
                        class="w-full max-w-sm"
                      />
                      @if (projectForm.get('tgeDate')?.invalid &&
                      projectForm.get('tgeDate')?.touched) {
                      <div class="mt-2">
                        <p-message severity="error">{{
                          getErrorMessage(
                            projectForm.get('tgeDate'),
                            'projectEdit.tgeDateAndTime' | translate
                          )
                        }}</p-message>
                      </div>
                      }
                    </div>
                  </div>

                  <div class="py-4">
                    <p-button
                      [label]="'projectEdit.back' | translate"
                      severity="secondary"
                      icon="pi pi-arrow-left"
                      (onClick)="activateCallback(2)"
                    />
                  </div>
                </ng-template>
              </p-step-panel>
            </p-step-item>
          </p-stepper>
          }
        </p-card>
      </div>

      <!-- Tabs Section -->
      <div class="w-full">
        <p-card>
          @if (loading()) {
          <!-- Skeleton loading for tabs -->
          <div class="flex gap-2 mb-4">
            <p-skeleton width="120px" height="2.5rem"></p-skeleton>
            <p-skeleton width="120px" height="2.5rem"></p-skeleton>
            <p-skeleton width="120px" height="2.5rem"></p-skeleton>
            <p-skeleton width="120px" height="2.5rem"></p-skeleton>
          </div>
          <!-- Skeleton for tab content -->
          <p-skeleton width="100%" height="400px"></p-skeleton>
          } @else {
          <p-tabs value="0" [lazy]="true" (onChange)="onTabChange($event)">
            <p-tablist class="w-full overflow-x-auto">
              <p-tab value="0">{{ 'projectEdit.projectOverview.title' | translate }}</p-tab>
              <p-tab value="1">{{ 'projectEdit.teamMembers.title' | translate }}</p-tab>
              <p-tab value="2" (click)="onChartTabClicked()">{{
                'projectEdit.vestingCategories.title' | translate
              }}</p-tab>
              <p-tab value="3">{{ 'projectEdit.updates.title' | translate }}</p-tab>
            </p-tablist>

            <p-tabpanels>
              <p-tabpanel value="0">
                <div class="flex flex-col gap-4">
                  <h3 class="section-title">
                    {{ 'projectEdit.projectOverview.description' | translate }}
                  </h3>

                  <!-- Two Column Layout -->
                  <div class="two-column-layout">
                    <!-- Left Sidebar - Overview Nav Items (Sticky) -->
                    <div class="nav-sidebar sticky top-6 self-start">
                      <div class="nav-items-container">
                        @for (navItem of overviewNavItems; track navItem.id; let i = $index) {
                        <app-overview-nav-item
                          [navItem]="navItem"
                          [index]="i"
                          [isSelected]="selectedOverviewIndex === i"
                          (navItemSelected)="onOverviewNavSelected($event)"
                        ></app-overview-nav-item>
                        }
                      </div>
                    </div>

                    <!-- Right Content - Multiple Editors -->
                    <div class="main-content">
                      <div class="flex flex-col gap-6">
                        @for (navItem of overviewNavItems; track navItem.id; let i = $index) {
                        <div [id]="'overview-section-' + i" class="scroll-mt-4">
                          <h4 class="text-heading-md mb-3">{{ navItem.title }}</h4>
                          <p-editor
                            [formControl]="
                              i === 0
                                ? ourStoryControl
                                : i === 1
                                ? problemControl
                                : i === 2
                                ? solutionControl
                                : i === 3
                                ? technologyControl
                                : i === 4
                                ? marketAudienceControl
                                : i === 5
                                ? businessModelControl
                                : roadmapControl
                            "
                            [style]="{ height: '320px' }"
                            [placeholder]="'projectEdit.projectOverview.placeholder' | translate"
                            [modules]="editorModules"
                          >
                          </p-editor>
                        </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </p-tabpanel>
              <p-tabpanel value="1">
                <div class="flex flex-col gap-6">
                  <div class="flex justify-between items-center mb-4">
                    <h3 class="section-title">
                      {{ 'projectEdit.teamMembers.title' | translate }}
                    </h3>
                    <p-button
                      [label]="'projectEdit.teamMembers.addMember' | translate"
                      icon="pi pi-plus"
                      (onClick)="openAddTeamMemberDialog()"
                      class="ml-auto"
                    />
                  </div>

                  <!-- Team Members List -->
                  @if (teamMembers.length > 0) {
                  <div class="flex flex-wrap gap-6">
                    @for (member of teamMembers; track member.id; let i = $index) {
                    <app-team-member-item
                      [member]="member"
                      [index]="i"
                      (editMember)="onEditMember($event)"
                      (removeMember)="removeTeamMember($event)"
                    ></app-team-member-item>
                    }
                  </div>
                  } @else {
                  <div class="empty-state">
                    <i class="pi pi-users empty-state-icon"></i>
                    <h4 class="empty-state-title">
                      {{ 'projectEdit.teamMembers.noMembers' | translate }}
                    </h4>
                    <p class="empty-state-description">
                      {{ 'projectEdit.teamMembers.noMembersDescription' | translate }}
                    </p>
                    <p-button
                      [label]="'projectEdit.teamMembers.addFirstMember' | translate"
                      icon="pi pi-plus"
                      (onClick)="openAddTeamMemberDialog()"
                    />
                  </div>
                  }
                </div>
              </p-tabpanel>
              <p-tabpanel value="2">
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <!-- Left Side: Token Allocation Chart -->
                  <div class="lg:col-span-1">
                    <p-card>
                      <h3 class="section-title mb-6">
                        {{ 'projectEdit.tokenAllocation.title' | translate }}
                      </h3>

                      <!-- Token Supply Info -->
                      <div class="space-y-2 mb-6">
                        <div class="flex justify-between text-sm">
                          <span class="text-gray-400">{{
                            'projectEdit.tokenAllocation.tokenSupply' | translate
                          }}</span>
                          <span class="font-semibold">{{
                            projectForm.get('totalSupply')?.value | number
                          }}</span>
                        </div>
                        <div class="flex justify-between text-sm">
                          <span class="text-gray-400">{{
                            'projectEdit.tokenAllocation.privateSale' | translate
                          }}</span>
                          <span class="font-semibold">{{
                            projectForm.get('softCap')?.value | number
                          }}</span>
                        </div>
                        <div class="flex justify-between text-sm">
                          <span class="text-gray-400">{{
                            'projectEdit.tokenAllocation.publicSale' | translate
                          }}</span>
                          <span class="font-semibold">{{
                            projectForm.get('hardCap')?.value | number
                          }}</span>
                        </div>
                      </div>

                      <!-- Doughnut Chart -->
                      <div class="mb-6 flex justify-center" #chartContainer>
                        @if (chartData && chartData.labels && chartData.labels.length > 0) { @for
                        (item of [1]; track chartData.labels?.length + '-' +
                        (chartData.datasets?.[0]?.data?.join(',') || '')) {
                        <p-chart
                          #tokenChart
                          type="doughnut"
                          [data]="chartData"
                          [options]="chartOptions"
                          width="150"
                          height="150"
                        ></p-chart>
                        } } @else {
                        <div
                          class="w-[150px] h-[150px] border-2 border-dashed border-gray-600 rounded-full flex items-center justify-center text-gray-500 text-sm"
                        >
                          No data
                        </div>
                        }
                      </div>

                      <!-- Add Allocation Button -->
                      <div class="mb-4">
                        <p-button
                          [label]="'projectEdit.tokenAllocation.addAllocation' | translate"
                          icon="pi pi-plus"
                          (onClick)="openAddAllocationDialog()"
                          [outlined]="true"
                          size="small"
                          class="w-full"
                        />
                      </div>

                      <!-- Token Allocation Chips -->
                      <div class="space-y-3">
                        <h4 class="text-sm font-semibold">
                          {{ 'projectEdit.tokenAllocation.allocations' | translate }}
                        </h4>

                        @if (tokenAllocationFormArray.length === 0) {
                        <div class="text-center py-4 text-gray-500 text-sm">
                          {{ 'projectEdit.tokenAllocation.emptyMessage' | translate }}
                        </div>
                        } @else {
                        <div class="flex flex-wrap gap-2">
                          @for (control of tokenAllocationFormArray.controls; track $index) {
                          <div
                            class="flex items-center gap-2 px-3 py-2 rounded-full bg-surface-100 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 cursor-pointer hover:bg-surface-200 dark:hover:bg-surface-700 transition-colors"
                            (click)="openEditAllocationDialog($index)"
                          >
                            <!-- Color Circle -->
                            <div
                              class="w-4 h-4 rounded-full flex-shrink-0"
                              [style.background-color]="$any(control.get('color'))?.value"
                            ></div>

                            <!-- Name and Percentage -->
                            <span
                              class="text-sm font-medium text-surface-900 dark:text-surface-100"
                            >
                              {{ $any(control.get('name'))?.value }} ({{
                                $any(control.get('percentage'))?.value
                              }}%)
                            </span>

                            <!-- Delete Button -->
                            <button
                              type="button"
                              class="ml-1 w-5 h-5 rounded-full flex items-center justify-center hover:bg-surface-300 dark:hover:bg-surface-600 transition-colors"
                              (click)="removeAllocationItem($index); $event.stopPropagation()"
                              [attr.aria-label]="'projectEdit.remove' | translate"
                            >
                              <i
                                class="pi pi-times text-xs text-surface-600 dark:text-surface-400"
                              ></i>
                            </button>
                          </div>
                          }
                        </div>

                        <!-- Total Percentage Display -->
                        <div
                          class="flex justify-between items-center pt-3 border-t border-gray-700"
                        >
                          <span class="font-semibold">{{
                            'projectEdit.tokenAllocation.total' | translate
                          }}</span>
                          <span
                            class="font-semibold"
                            [class.text-red-500]="getTotalAllocationPercentage() !== 100"
                            [class.text-green-500]="getTotalAllocationPercentage() === 100"
                          >
                            {{ getTotalAllocationPercentage() }}%
                          </span>
                        </div>
                        }
                      </div>
                    </p-card>
                  </div>

                  <!-- Right Side: Vesting Categories Table -->
                  <div class="lg:col-span-2">
                    <div class="flex justify-between items-center mb-6">
                      <h3 class="section-title">
                        {{ 'projectEdit.vestingCategories.title' | translate }}
                      </h3>
                      <p-button
                        [label]="'projectEdit.vestingCategories.addButton' | translate"
                        icon="pi pi-plus"
                        (onClick)="openAddVestingCategoryDialog()"
                      >
                      </p-button>
                    </div>

                    <p-table
                      [value]="vestingCategoriesFormArray.controls"
                      [tableStyle]="{ 'min-width': '50rem' }"
                    >
                      <ng-template pTemplate="header">
                        <tr>
                          <th>{{ 'projectEdit.vestingCategories.categoryLabel' | translate }}</th>
                          <th>
                            {{ 'projectEdit.vestingCategories.cliffPeriodLabel' | translate }}
                          </th>
                          <th>
                            {{ 'projectEdit.vestingCategories.vestingDurationLabel' | translate }}
                          </th>
                          <th>
                            {{
                              'projectEdit.vestingCategories.releasingFrequencyLabel' | translate
                            }}
                          </th>
                          <th>{{ 'projectEdit.vestingCategories.tgeUnlockLabel' | translate }}</th>
                          <th>{{ 'projectEdit.vestingCategories.firstClaimLabel' | translate }}</th>
                          <th>{{ 'projectEdit.actions' | translate }}</th>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-control let-i="rowIndex">
                        <tr>
                          <td>
                            <div class="flex flex-col gap-1">
                              <div class="font-semibold">{{ control.get('category')?.value }}</div>
                              <div class="flex items-center gap-2 text-sm text-gray-400">
                                <span class="font-mono">{{
                                  control.get('walletAddress')?.value
                                }}</span>
                                <button
                                  type="button"
                                  class="pi pi-copy text-xs hover:text-white cursor-pointer"
                                  (click)="copyToClipboard(control.get('walletAddress')?.value)"
                                  [attr.aria-label]="'Copy wallet address'"
                                ></button>
                              </div>
                            </div>
                          </td>
                          <td>{{ control.get('cliffPeriod')?.value }}</td>
                          <td>{{ control.get('vestingDuration')?.value }}</td>
                          <td>{{ control.get('releasingFrequency')?.value }}</td>
                          <td>{{ control.get('tgeUnlock')?.value }}%</td>
                          <td>{{ control.get('firstClaim')?.value }}</td>
                          <td>
                            <div class="flex flex-row">
                              <p-button
                                icon="pi pi-pencil"
                                [rounded]="true"
                                size="small"
                                class="mr-2"
                                (onClick)="editVestingCategory(i)"
                                [ariaLabel]="'projectEdit.edit' | translate"
                              >
                              </p-button>
                              <p-button
                                icon="pi pi-trash"
                                [rounded]="true"
                                size="small"
                                (onClick)="removeVestingCategory(i)"
                                [ariaLabel]="'projectEdit.remove' | translate"
                              >
                              </p-button>
                            </div>
                          </td>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="emptymessage">
                        <tr>
                          <td colspan="7" class="text-center py-4 text-gray-500">
                            {{ 'projectEdit.vestingCategories.emptyMessage' | translate }}
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  </div>
                </div>
              </p-tabpanel>
              <p-tabpanel value="3">
                <div class="flex flex-col gap-6">
                  <div class="flex justify-between items-center mb-4">
                    <h3 class="section-title">
                      {{ 'projectEdit.updates.title' | translate }}
                    </h3>
                    <p-button
                      [label]="'projectEdit.updates.addUpdate' | translate"
                      icon="pi pi-plus"
                      (onClick)="openAddUpdateDialog()"
                      class="ml-auto"
                    />
                  </div>

                  <!-- Updates List in Single Column Layout -->
                  @if (updates.length > 0) {
                  <div class="flex flex-col gap-6">
                    @for (update of updates; track update.id; let i = $index) {
                    <app-update-item
                      [update]="update"
                      [index]="i"
                      (editUpdate)="onEditUpdate($event)"
                      (removeUpdate)="removeUpdate($event)"
                    ></app-update-item>
                    }
                  </div>
                  } @else {
                  <div class="empty-state">
                    <i class="pi pi-megaphone empty-state-icon"></i>
                    <h4 class="empty-state-title">
                      {{ 'projectEdit.updates.noUpdates' | translate }}
                    </h4>
                    <p class="empty-state-description">
                      {{ 'projectEdit.updates.noUpdatesDescription' | translate }}
                    </p>
                    <p-button
                      [label]="'projectEdit.updates.addFirstUpdate' | translate"
                      icon="pi pi-plus"
                      (onClick)="openAddUpdateDialog()"
                    />
                  </div>
                  }
                </div>
              </p-tabpanel>
            </p-tabpanels>
          </p-tabs>
          }
        </p-card>

        <!-- Action Buttons -->
        <div class="flex flex-col md:flex-row gap-4 justify-end my-6">
          <p-button
            type="button"
            label="Fill Test Data"
            severity="help"
            [outlined]="true"
            icon="pi pi-file-import"
            (onClick)="fillTestData()"
          />
          <p-button
            type="button"
            [label]="'projectEdit.cancel' | translate"
            severity="secondary"
            [outlined]="true"
            [disabled]="submitting()"
            (onClick)="onCancel()"
          />
          <p-button
            type="submit"
            [label]="
              submitting()
                ? projectId === '1'
                  ? ('projectEdit.creating' | translate)
                  : ('projectEdit.saving' | translate)
                : projectId === '1'
                ? ('projectEdit.create' | translate)
                : ('projectEdit.save' | translate)
            "
            [icon]="
              submitting()
                ? 'pi pi-spin pi-spinner'
                : projectId === '1'
                ? 'pi pi-plus'
                : 'pi pi-save'
            "
            [disabled]="projectForm.invalid || submitting()"
            [loading]="submitting()"
          />
        </div>
      </div>
    </form>
  </div>
</div>
